
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    TextField,
    MenuItem,
    Card,
    CardContent,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Divider,
    TablePagination
} from "@mui/material";
import {
    History as HistoryIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";
import AppHeader from "../../components/AppHeader";
import { formatYangon } from "../../services/datetimeService";

const LeaveAdjustment = ({ setIsLoggedIn }) => {
    const [staffs, setStaffs] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [logs, setLogs] = useState([]);
    const [adjData, setAdjData] = useState({
        staff_id: "",
        leave_type_id: "",
        year: new Date().getFullYear(),
        adjustment_days: "",
        reason: "",
        type: "manual"
    });
    const [loading, setLoading] = useState(false);
    const [logsLoading, setLogsLoading] = useState(false);
    const [feedback, setFeedback] = useState({ open: false, message: "", severity: "success" });

    // Edit state
    const [editOpen, setEditOpen] = useState(false);
    const [editingLog, setEditingLog] = useState(null);
    const [editData, setEditData] = useState({ adjustment_days: "", reason: "" });

    // Pagination & Filter state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [filterSearch, setFilterSearch] = useState("");
    const [filterStaffId, setFilterStaffId] = useState("");
    const [filterLeaveTypeId, setFilterLeaveTypeId] = useState("");
    const [filterYear, setFilterYear] = useState("");

    const tenant = localStorage.getItem("tenant") || "";

    const loadLookups = useCallback(async () => {
        try {
            const [staffRes, typeRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff`, { withCredentials: true, headers: { "X-Tenant": tenant } }),
                axios.get(`${process.env.REACT_APP_API_URL1}/leave/types`, { withCredentials: true, headers: { "X-Tenant": tenant } })
            ]);

            const staffList = Array.isArray(staffRes.data)
                ? staffRes.data
                : (staffRes.data.staff || staffRes.data.rows || []);

            setStaffs(staffList);
            if (typeRes.data.success) setLeaveTypes(typeRes.data.data || []);
        } catch (err) {
            console.error("Load lookups error:", err);
        }
    }, [tenant]);

    const loadLogs = useCallback(async () => {
        setLogsLoading(true);
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage
            };
            if (filterSearch) params.search = filterSearch;
            if (filterStaffId) params.staff_id = filterStaffId;
            if (filterLeaveTypeId) params.leave_type_id = filterLeaveTypeId;
            if (filterYear) params.year = filterYear;

            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/leave/admin/adjust/logs`, {
                params,
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });
            if (res.data.success) {
                setLogs(res.data.data || []);
                setTotalCount(res.data.pagination?.total || 0);
            }
        } catch (err) {
            console.error("Load logs error:", err);
        }
        setLogsLoading(false);
    }, [tenant, page, rowsPerPage, filterSearch, filterStaffId, filterLeaveTypeId, filterYear]);

    useEffect(() => {
        loadLookups();
        loadLogs();
    }, [loadLookups, loadLogs]);

    const handleClearFilters = () => {
        setFilterSearch("");
        setFilterStaffId("");
        setFilterLeaveTypeId("");
        setFilterYear("");
        setPage(0);
    };

    const handleAdjust = async () => {
        if (!adjData.staff_id || !adjData.leave_type_id || !adjData.adjustment_days) {
            setFeedback({ open: true, message: "Please fill all required fields", severity: "warning" });
            return;
        }

        setLoading(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/leave/admin/adjust`, adjData, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });

            if (res.data.success) {
                setFeedback({ open: true, message: "Balance adjusted successfully", severity: "success" });
                setAdjData({
                    ...adjData,
                    staff_id: "",
                    leave_type_id: "",
                    adjustment_days: "",
                    reason: ""
                });
                if (page === 0) {
                    loadLogs();
                } else {
                    setPage(0);
                }
            } else {
                setFeedback({ open: true, message: res.data.message || "Adjustment failed", severity: "error" });
            }
        } catch (err) {
            console.error("Adjustment error:", err);
            setFeedback({ open: true, message: "Error communicating with server", severity: "error" });
        }
        setLoading(false);
    };

    const handleUpdate = async () => {
        if (!editingLog || editData.adjustment_days === "") return;
        setLoading(true);
        try {
            const res = await axios.put(`${process.env.REACT_APP_API_URL1}/leave/admin/adjust/${editingLog.id}`, editData, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });
            if (res.data.success) {
                setFeedback({ open: true, message: "Adjustment updated", severity: "success" });
                setEditOpen(false);
                loadLogs();
            } else {
                setFeedback({ open: true, message: res.data.message || "Update failed", severity: "error" });
            }
        } catch (err) {
            setFeedback({ open: true, message: "Server error", severity: "error" });
        }
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this adjustment? This will also revert the staff's balance.")) return;
        setLoading(true);
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/leave/admin/adjust/${id}`, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });
            if (res.data.success) {
                setFeedback({ open: true, message: "Adjustment deleted", severity: "success" });
                loadLogs();
            } else {
                setFeedback({ open: true, message: res.data.message || "Delete failed", severity: "error" });
            }
        } catch (err) {
            setFeedback({ open: true, message: "Server error", severity: "error" });
        }
        setLoading(false);
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa" }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                    <HistoryIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold" color="text.primary">
                        Manual Leave Balance Adjustment
                    </Typography>
                </Stack>

                <Grid container spacing={3}>
                    {/* Adjustment Form */}
                    <Grid item xs={12} lg={4}>
                        <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", position: 'sticky', top: 20 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>New Adjustment</Typography>
                                <Stack spacing={2.5}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Select Staff Member"
                                        size="small"
                                        value={adjData.staff_id}
                                        onChange={(e) => setAdjData({ ...adjData, staff_id: e.target.value })}
                                        variant="outlined"
                                    >
                                        {staffs.map((s) => (
                                            <MenuItem key={s.staff_id} value={s.staff_id}>
                                                {s.full_name} ({s.staff_code})
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        select
                                        fullWidth
                                        label="Leave Type"
                                        size="small"
                                        value={adjData.leave_type_id}
                                        onChange={(e) => setAdjData({ ...adjData, leave_type_id: e.target.value })}
                                        variant="outlined"
                                    >
                                        {leaveTypes.map((t) => (
                                            <MenuItem key={t.id} value={t.id}>
                                                {t.type_name} ({t.type_code})
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Year"
                                            size="small"
                                            type="number"
                                            value={adjData.year}
                                            onChange={(e) => setAdjData({ ...adjData, year: e.target.value })}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Days"
                                            size="small"
                                            type="number"
                                            placeholder="+/-"
                                            value={adjData.adjustment_days}
                                            onChange={(e) => setAdjData({ ...adjData, adjustment_days: e.target.value })}
                                        />
                                    </Stack>

                                    <TextField
                                        fullWidth
                                        label="Reason"
                                        size="small"
                                        multiline
                                        rows={2}
                                        placeholder="Reason for adjustment..."
                                        value={adjData.reason}
                                        onChange={(e) => setAdjData({ ...adjData, reason: e.target.value })}
                                    />

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={<SaveIcon />}
                                        onClick={handleAdjust}
                                        disabled={loading}
                                        sx={{
                                            py: 1.2,
                                            borderRadius: 2,
                                            fontWeight: "bold",
                                            textTransform: "none"
                                        }}
                                    >
                                        {loading ? "Processing..." : "Submit Adjustment"}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Adjustment History Table */}
                    <Grid item xs={12} lg={8}>
                        <Paper sx={{ borderRadius: 2, p: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" fontWeight="bold">Recent Adjustments</Typography>
                                <Button
                                    size="small"
                                    startIcon={<RefreshIcon />}
                                    onClick={loadLogs}
                                    disabled={logsLoading}
                                >
                                    Refresh
                                </Button>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {/* Filter Section */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Search Staff"
                                        placeholder="Name or Code..."
                                        size="small"
                                        value={filterSearch}
                                        onChange={(e) => {
                                            setFilterSearch(e.target.value);
                                            setPage(0);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Filter Staff"
                                        size="small"
                                        value={filterStaffId}
                                        onChange={(e) => {
                                            setFilterStaffId(e.target.value);
                                            setPage(0);
                                        }}
                                    >
                                        <MenuItem value="">All Staff</MenuItem>
                                        {staffs.map((s) => (
                                            <MenuItem key={s.staff_id} value={s.staff_id}>
                                                {s.full_name} ({s.staff_code})
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Filter Leave Type"
                                        size="small"
                                        value={filterLeaveTypeId}
                                        onChange={(e) => {
                                            setFilterLeaveTypeId(e.target.value);
                                            setPage(0);
                                        }}
                                    >
                                        <MenuItem value="">All Types</MenuItem>
                                        {leaveTypes.map((t) => (
                                            <MenuItem key={t.id} value={t.id}>
                                                {t.type_code}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="secondary"
                                        size="medium"
                                        onClick={handleClearFilters}
                                        sx={{ height: 40, textTransform: "none" }}
                                    >
                                        Clear
                                    </Button>
                                </Grid>
                            </Grid>

                            <TableContainer sx={{ maxHeight: 600 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Staff</TableCell>
                                            <TableCell>Leave Type</TableCell>
                                            <TableCell align="center">Days</TableCell>
                                            <TableCell>Reason</TableCell>
                                            <TableCell>By / Date</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {logs.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                                    <Typography color="text.secondary">No adjustment records found.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            logs.map((log) => (
                                                <TableRow key={log.id} hover>
                                                    <TableCell sx={{ minWidth: 150 }}>
                                                        <Typography variant="body2" fontWeight="bold">{log.full_name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{log.staff_code}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={log.type_code} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="bold"
                                                            color={log.adjustment_days > 0 ? "success.main" : "error.main"}
                                                        >
                                                            {log.adjustment_days > 0 ? `+${log.adjustment_days}` : log.adjustment_days}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {log.reason}
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        <Typography variant="caption" sx={{ display: 'block' }}>{log.created_by_name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatYangon(log.created_at)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={0} justifyContent="flex-end">
                                                            <Tooltip title="Edit">
                                                                <IconButton size="small" color="primary" onClick={() => {
                                                                    setEditingLog(log);
                                                                    setEditData({ adjustment_days: log.adjustment_days, reason: log.reason });
                                                                    setEditOpen(true);
                                                                }}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete">
                                                                <IconButton size="small" color="error" onClick={() => handleDelete(log.id)}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(event, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(event) => {
                                    setRowsPerPage(parseInt(event.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Adjustment</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Target: <strong>{editingLog?.full_name}</strong> - {editingLog?.type_name} ({editingLog?.year})
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Adjustment Days"
                                type="number"
                                size="small"
                                value={editData.adjustment_days}
                                onChange={(e) => setEditData({ ...editData, adjustment_days: e.target.value })}
                            />
                            <TextField
                                fullWidth
                                label="Reason"
                                size="small"
                                multiline
                                rows={3}
                                value={editData.reason}
                                onChange={(e) => setEditData({ ...editData, reason: e.target.value })}
                            />
                        </Stack>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEditOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleUpdate} variant="contained" disabled={loading} startIcon={<SaveIcon />}>
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={feedback.open}
                autoHideDuration={6000}
                onClose={() => setFeedback({ ...feedback, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setFeedback({ ...feedback, open: false })} severity={feedback.severity} sx={{ width: '100%' }}>
                    {feedback.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LeaveAdjustment;
